
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { resetSessionState, forceSessionRefresh } from "@/services/auth/sessionUtils";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('useSessionRefresh');

// Global refresh state to prevent duplicate refreshes across components
const globalRefreshState = {
  inProgress: false,
  lastRefresh: 0
};

export const useSessionRefresh = () => {
  const [sessionRefreshAttempts, setSessionRefreshAttempts] = useState(0);
  const initialized = useRef(false);
  const isMountedRef = useRef(true);

  // Handle periodic session refresh
  useEffect(() => {
    if (initialized.current) {
      return;
    }
    
    initialized.current = true;
    let refreshInterval: number | undefined;
    
    const refreshSessionOnMount = async () => {
      try {
        resetSessionState();
        
        if (!isMountedRef.current) return;
        
        logger.debug("Initializing session on app mount");
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error && isMountedRef.current) {
          logger.error("Error refreshing session on mount:", error);
        } else if (data.session && isMountedRef.current) {
          logger.debug("Session successfully refreshed on application mount");
          
          // Set a longer refresh interval (10 minutes)
          refreshInterval = window.setInterval(() => {
            if (isMountedRef.current && !globalRefreshState.inProgress) {
              const now = Date.now();
              
              // Skip if a refresh was performed recently
              if (now - globalRefreshState.lastRefresh < 60000) {
                return;
              }
              
              logger.debug("Performing scheduled session refresh");
              globalRefreshState.inProgress = true;
              
              forceSessionRefresh().then(session => {
                if (session) {
                  logger.debug("Scheduled session refresh successful");
                  setSessionRefreshAttempts(0);
                  globalRefreshState.lastRefresh = Date.now();
                } else {
                  logger.warn("Scheduled session refresh failed");
                  setSessionRefreshAttempts(prev => prev + 1);
                }
                globalRefreshState.inProgress = false;
              });
            }
          }, 10 * 60 * 1000); // 10 minutes
        }
      } catch (err) {
        logger.error("Exception refreshing session on mount:", err);
      }
    };
    
    // Add a small delay to avoid initialization race conditions
    const timer = setTimeout(() => {
      refreshSessionOnMount();
    }, 500);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, []);

  // Manual session refresh function
  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (globalRefreshState.inProgress) {
      logger.debug("Refresh already in progress, skipping");
      return false;
    }
    
    try {
      globalRefreshState.inProgress = true;
      logger.debug("Manual session refresh requested");
      resetSessionState();
      setSessionRefreshAttempts(0);
      
      const session = await forceSessionRefresh();
      globalRefreshState.lastRefresh = Date.now();
      return !!session;
    } catch (error) {
      logger.error("Exception in manual session refresh:", error);
      toast.error("An error occurred while refreshing your session");
      return false;
    } finally {
      globalRefreshState.inProgress = false;
    }
  }, []);

  return {
    sessionRefreshAttempts,
    setSessionRefreshAttempts,
    refreshSession
  };
};
